import React, { useState, useMemo, useEffect } from "react";
import { useStoreActions, useStoreState } from "easy-peasy";
import { formatCurrencyUSD } from "utils/CommonUtils";
import MDBox from "components/MDBox";
import CollapsibleCard from "components/CollapsibleCard";
import { useSubmissionStore } from "models/applications/SubmissionStore";
import MDButton from "components/MDButton";
import GenericEmailSenderModal from "pages/applications/components/GenericEmailSenderModel";
import OffersTable from "./OffersTable";
import FundingChart from "./FundingChart";
import FullPageLoader from "components/Loader/FullPage";
import { Icon, IconButton, Tooltip } from "@mui/material";

interface OffersProps {
  dealApplicationId: number;
  setComponentLoading?: (componentName: string, isLoading: boolean) => void;
}

const Offers: React.FC<OffersProps> = ({ dealApplicationId, setComponentLoading }) => {
  const funders = useSubmissionStore()?.funderList?.map((funder) => {
    return {
      label: funder.name,
      value: funder.id,
    };
  });

  const offersData = useStoreState((state: any) => state.offerStore.offers);
  const contractTerms = useStoreState((state: any) => state.offerStore.contractTerms);
  const [loading, setLoading] = useState<boolean>(false);
  const averageTrueDeposits = useStoreState((state: any) => state.offerStore.averageTrueDeposits);
  const mcaHoldbackPercentage = useStoreState(
    (state: any) => state.offerStore.mcaHoldbackPercentage
  );
  const selectedRows = useStoreState((state: any) => state.offerStore.selectedRows);

  const deleteOffer = useStoreActions((actions: any) => actions.offerStore.deleteOffer);
  const fetchOffers = useStoreActions((actions: any) => actions.offerStore.fetchOffers);
  const onInit = useStoreActions((actions: any) => actions.offerStore.onInit);
  const getContractTerms = useStoreActions((actions: any) => actions.offerStore.getContractTerms);
  const addOffer = useStoreActions((actions: any) => actions.offerStore.createOrUpdateOffer);
  const saveFinalOffer = useStoreActions((actions: any) => actions.offerStore.saveContractTerms);
  const setSelectedRows = useStoreActions((actions: any) => actions.offerStore.setSelectedRows);

  const fetchRecalculatedApplication = useStoreActions(
    (actions: any) => actions.offerStore.fetchRecalculatedApplication
  );

  const handleBankAmountUpdate = useStoreActions(
    (actions: any) => actions.heldBackCommissionStore.handleForTotalBankFeeOrInvestorEditChanges
  );
  const updateSyndicatorsPercentage = useStoreActions(
    (actions: any) => actions.syndicationStore.updateSyndicatorsPercentage
  );

  //heldack commission syndication
  const refreshHeldabckComm = useStoreActions(
    (actions: any) => actions.heldBackCommissionStore.refresh
  );
  const refreshSynd = useStoreActions((actions: any) => actions.syndicationStore.fetchSyndicators);

  const [editingRow, setEditingRow] = useState<any | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [sortedColumn, setSortedColumn] = useState<string | null>("status");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [offers, setOffers] = useState(offersData);
  const [showSendOffer, setShowSendOffer] = useState(false);
  const [isViewEditMode, setIsViewEditMode] = useState(false);
  const [scheduleFrequency, setScheduleFrequency] = useState("weekly");
  const [mode, setMode] = useState("");
  const externalFunders = useStoreState(
    (state: any) => state.heldBackCommissionStore.externalFunders
  );
  const heldbacksData = useStoreState((state: any) => state.heldBackCommissionStore.heldbacks);
  const deleteHeldBack = useStoreActions(
    (actions: any) => actions.heldBackCommissionStore.deleteCommission
  );
  const saveHeldback = useStoreActions(
    (actions: any) => actions.heldBackCommissionStore.saveHeldback
  );
  const getExternalFunders = useStoreActions(
    (actions: any) => actions.heldBackCommissionStore.getExternalFunders
  );

  const [formData, setFormData] = useState({
    fundingAmount: 0,
    factor: 0,
    purchasePrice: 0,
    bankFeePercentage: 0,
    bankFeeAmount: 0,
    totalCommissionPercentage: 0,
    totalCommissionAmount: 0,
    totalRefinance: 0,
    totalExternalPayoff: 0,
    totalFees: 0,
    leverage: 0,
    position: 1,
    specifiedPercentage: 0,
    fundingDate: new Date().toISOString().split("T")[0],
  });

  const [debits, setDebits] = useState<any>({
    daily: {
      recurringAmount: "",
      numTerms: "",
      totalBankDays: "",
      lastPaymentDate: "",
    },
    weekly: {
      recurringAmount: "",
      numTerms: "",
      totalBankDays: "",
      lastPaymentDate: "",
    },
    monthly: {
      recurringAmount: "",
      numTerms: "",
      totalBankDays: "",
      lastPaymentDate: "",
    },
  });

  useEffect(() => {
    try {
      setComponentLoading("offers", true);
      setSortedColumn("status");
      setSortDirection("asc");
      onInit(dealApplicationId);
      getExternalFunders();
    } finally {
      setComponentLoading("offers", false);
    }
  }, []);

  useEffect(() => {
    setOffers(offersData);
  }, [offersData]);

  const handleEditStatusInline = (row: any) => {
    const offerToEdit = offersData.find((offer: any) => offer.offerKey === row.offerKey);
    if (offerToEdit) {
      const cleanedOffer = { ...offerToEdit };

      const numericFieldsToNormalize = [
        "leverage",
        "term",
        "bankFeeAmount",
        "specifiedPercentage",
        "otherFees",
        "numberOfPayments",
        "recurringAmount",
        "weeklyAmount",
        "bankFeePercentage",
        "refinanceAmount",
        "commissionPercentage",
        "paybackAmount",
        "externalPayOffAmount",
        "monthlyAmount",
        "weeklyAmount",
        "commissionAmount",
      ];

      numericFieldsToNormalize.forEach((field) => {
        const value = cleanedOffer[field];
        if (value === null || value === "-" || isNaN(Number(value))) {
          cleanedOffer[field] = 0;
        }
      });

      if (cleanedOffer.estimatedPayoffDate === "-") {
        cleanedOffer.estimatedPayoffDate = "";
      }

      const updatedOffer = {
        ...cleanedOffer,
        oldStatus: offerToEdit.status,
      };

      setEditingRow(updatedOffer);
      setIsEditing(true);
    }
  };

  const columns = [
    {
      Header: "FUNDER",
      accessor: "funderName",
      selector: "funderId",
      align: "left",
      width: 300,
      input: "select",
      options: funders,
      required: false,
    },
    {
      Header: "STATUS",
      accessor: "status",
      width: 150,
      input: "select",
      align: "left",
      options: [
        { label: "Not Sent", value: "Not Sent" },
        { label: "Accepted", value: "Accepted" },
        { label: "Sent", value: "Sent" },
        { label: "Declined", value: "Declined" },
        { label: "Timed Out", value: "Timed Out" },
        { label: "Pending Response", value: "Pending Response" },
        { label: "Response Sent", value: "Response Sent"}
      ],
      required: false,
      Cell: ({ row }: any) => (
        <MDBox display="flex" alignItems="center" gap={1}>
          {row?.status}
          <Tooltip title="Edit Status">
            <IconButton size="small" onClick={() => handleEditStatusInline(row)}>
              <Icon sx={{ fontSize: 14 }}>edit</Icon>
            </IconButton>
          </Tooltip>
        </MDBox>
      ),
    },
    {
      Header: "TYPE",
      accessor: "type",
      width: 120,
      input: "select",
      options: [{ label: "Straight", value: "Straight" }],
      required: false,
      disabled: () => true,
    },
    {
      Header: "FUNDING AMOUNT",
      accessor: "fundingAmount",
      width: 150,
      input: "amount",
      required: false,
      disabled: () => true,
      Cell: (row: any) => formatCurrencyUSD(row?.row?.fundingAmount),
    },
    {
      Header: "PAYBACK AMOUNT",
      accessor: "paybackAmount",
      width: 150,
      input: "amount",
      required: false,
      disabled: () => true,

      Cell: (row: any) => formatCurrencyUSD(row?.row?.paybackAmount),
    },
    {
      Header: "FACTOR",
      accessor: "factor",
      input: "number",
      required: false,
      disabled: () => true,
      width: 100,
    },
    {
      Header: "FREQUENCY",
      accessor: "frequency",
      input: "select",
      required: false,
      disabled: () => true,
      options: [
        { label: "Daily", value: "daily" },
        { label: "Monthly", value: "monthly" },
        { label: "Weekly", value: "weekly" },
      ],
      width: 120,
      Cell: (row: any) => {
        const frequency = row?.row?.frequency;
        return frequency ? frequency.charAt(0).toUpperCase() + frequency.slice(1) : "";
      },
    },
    {
      Header: "DEBIT AMOUNT",
      accessor: "recurringAmount",
      width: 120,
      required: false,
      disabled: () => true,
      input: "amount",
      Cell: (row: any) => formatCurrencyUSD(row?.row?.recurringAmount),
    },
    {
      Header: "AVG. WEEKLY AMOUNT",
      accessor: "weeklyAmount",
      width: 120,
      required: false,
      disabled: () => true,
      input: "amount",
      Cell: (row: any) =>
        isNaN(row?.row?.weeklyAmount) || !row?.row?.weeklyAmount
          ? "-"
          : formatCurrencyUSD(row?.row?.weeklyAmount),
    },
    {
      Header: "AVG. MONTHLY AMOUNT",
      accessor: "monthlyAmount",
      width: 120,
      required: false,
      disabled: () => true,
      input: "amount",
      Cell: (row: any) =>
        isNaN(row?.row?.monthlyAmount) || !row?.row?.monthlyAmount
          ? "-"
          : formatCurrencyUSD(row?.row?.monthlyAmount),
    },
    {
      Header: "LEVERAGE",
      accessor: "leverage",
      input: "number",
      width: 80,
      required: false,
      disabled: () => true,
    },
    {
      Header: "EST. TERM",
      accessor: "term",
      input: "number",
      required: false,
      width: 80,
      disabled: () => true,
    },
  ];

  const closeSendModel = () => {
    setShowSendOffer(false);
  };

  const handleAddOffer = () => {
    setIsViewEditMode(true);
    setMode("add");
  };

  const handleDuplicateOffer = () => {
    if (selectedRows.length === 1) {
      const offerToDuplicate = offers.find((offer: any) => offer.offerKey === selectedRows[0]);

      if (offerToDuplicate) {
        const { offerKey, ...offerWithoutKey } = offerToDuplicate;
        setEditingRow({
          ...offerWithoutKey,
        });
        setIsEditing(true);
      }
    }
  };

  const handleEditStatus = () => {
    if (selectedRows.length === 1) {
      const offerToEdit = offers.find((offer: any) => offer.offerKey === selectedRows[0]);

      if (offerToEdit) {
        const cleanedOffer = { ...offerToEdit };

        const numericFieldsToNormalize = [
          "leverage",
          "term",
          "bankFeeAmount",
          "specifiedPercentage",
          "otherFees",
          "numberOfPayments",
          "recurringAmount",
          "weeklyAmount",
          "bankFeePercentage",
          "refinanceAmount",
          "commissionPercentage",
          "paybackAmount",
          "externalPayOffAmount",
          "commissionAmount",
        ];

        numericFieldsToNormalize.forEach((field) => {
          const value = cleanedOffer[field];
          if (value === null || value === "-" || isNaN(Number(value))) {
            cleanedOffer[field] = 0;
          }
        });

        if (cleanedOffer.estimatedPayoffDate === "-") {
          cleanedOffer.estimatedPayoffDate = "";
        }

        const updatedOffer = {
          ...cleanedOffer,
          oldStatus: offerToEdit.status,
        };

        setEditingRow(updatedOffer);
        setIsEditing(true);
      }
    }
  };

  const scoreboard = useStoreState((state: any) => state.bankSummaryStore.scoreboard);

  const customButton = (
    <MDBox display="flex" gap={2}>
      {selectedRows && selectedRows?.length == 1 && (
        <MDButton
          color="primary"
          variant="contained"
          size="medium"
          sx={{ textTransform: "none" }}
          onClick={handleEditStatus}
        >
          Edit Status
        </MDButton>
      )}

      {selectedRows && selectedRows?.length == 1 && (
        <MDButton
          color="primary"
          variant="contained"
          size="medium"
          sx={{ textTransform: "none" }}
          onClick={handleDuplicateOffer}
        >
          Duplicate Offer
        </MDButton>
      )}
      <MDButton
        color="primary"
        variant="contained"
        size="medium"
        sx={{ textTransform: "none" }}
        onClick={handleAddOffer}
      >
        + Add Offer
      </MDButton>
    </MDBox>
  );

  const handleDeleteOffer = async (offerKey: number) => {
    await deleteOffer({
      dealApplicationId: dealApplicationId,
      offerId: offerKey,
    });
    const row = offersData.find((offer: any) => offer.offerKey === offerKey);
    if (row.status === "Accepted") {
      await clearAcceptedOffer();
      await fetchRecalculatedApplication(dealApplicationId);
      await updateSyndicatorsPercentage({
        applicationId: dealApplicationId,
        reload: false,
      });
    }
    await onInit(dealApplicationId);
    await refreshHeldabckComm(dealApplicationId);
    await refreshSynd(dealApplicationId);
  };

  const sanitizeAndToFloat = (value: any) => {
    if (!value) {
      return 0;
    }

    const stringValue = String(value);
    const sanitizedValue = stringValue.replace(/[^0-9.-]/g, "");
    const floatValue = parseFloat(sanitizedValue);
    return isNaN(floatValue) ? 0 : floatValue;
  };

  const toNumber = (value: any) => {
    return isNaN(Number(value)) ? 0 : Number(value);
  };

  const handleEditOffer = (row: any | null) => {
    const freq = row?.frequency || "daily";
    setScheduleFrequency(freq);

    const originalData: any = {
      fundingAmount: row?.fundingAmount || 1,
      factor: row?.factor || 1,
      purchasePrice: row?.paybackAmount || 1,
      bankFeePercentage: row?.bankFeePercentage || 0,
      bankFeeAmount: row?.bankFeeAmount || 0,
      totalCommissionPercentage: row?.commissionPercentage || 0,
      totalCommissionAmount: row?.commissionAmount || 0,
      totalRefinance: row.refinanceAmount || 0,
      totalExternalPayoff: row.externalPayOffAmount || 0,
      totalFees: row.otherFees || 0,
      leverage: row.leverage && !isNaN(row.leverage) ? row.leverage : 0,
      specifiedPercentage:
        row.specifiedPercentage && !isNaN(row.specifiedPercentage) ? row.specifiedPercentage : 0,
      fundingDate: row.fundingDate || new Date().toISOString().split("T")[0],
      position: row.position || 0,
    };

    if (row?.offerKey) {
      originalData.offerKey = row?.offerKey;
    }

    if (row?.funderId) {
      originalData.funderId = row?.funderId;
    }

    if (row?.funderName) {
      originalData.funderName = row?.funderName;
    }

    if (row?.type) {
      originalData.type = row?.type;
    }

    if (row?.status) {
      originalData.status = row?.status;
      offersData.oldStatus = row.status;
    }

    const originalSchedules = {
      daily: {
        recurringAmount: freq === "daily" ? sanitizeAndToFloat(row?.recurringAmount) : 0,
        numTerms: freq === "daily" ? toNumber(row?.term) : 0,
        totalBankDays: freq === "daily" ? toNumber(row?.numberOfPayments) : 0,
        lastPaymentDate: freq === "daily" ? row?.estimatedPayoffDate || "" : "",
      },
      weekly: {
        recurringAmount: freq === "weekly" ? sanitizeAndToFloat(row?.recurringAmount) : 0,
        numTerms: freq === "weekly" ? toNumber(row?.term) : 0,
        totalBankDays: freq === "weekly" ? toNumber(row?.numberOfPayments) : 0,
        lastPaymentDate: freq === "weekly" ? row?.estimatedPayoffDate || "" : "",
      },
      monthly: {
        recurringAmount: freq === "monthly" ? sanitizeAndToFloat(row?.recurringAmount) : 0,
        numTerms: freq === "monthly" ? toNumber(row?.term) : 0,
        totalBankDays: freq === "monthly" ? toNumber(row?.numberOfPayments) : 0,
        lastPaymentDate: freq === "monthly" ? row?.estimatedPayoffDate || "" : "",
      },
    };
    setDebits(originalSchedules);
    setFormData(originalData);
    setIsViewEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsViewEditMode(false);
    setIsEditing(false);
    setEditingRow(null);
  };

  const handleAcceptedOffer = async (acceptedOffer: any) => {
    const newContractTerms = contractTerms;
    newContractTerms.applicationId = dealApplicationId;
    newContractTerms.tenantId = 1;
    newContractTerms.bankFee = acceptedOffer?.bankFeeAmount;
    newContractTerms.bankFeePercentage = acceptedOffer?.bankFeePercentage;
    newContractTerms.commissionTotal = acceptedOffer?.commissionAmount;
    newContractTerms.bankFeeCalculationThrough = "bank_fee_percentage";
    newContractTerms.paybackAmountCalculationThrough = "payback_factor";

    newContractTerms.contractTerms.collectionFrequency = acceptedOffer?.frequency;
    newContractTerms.contractTerms.collectionRecurringAmount = acceptedOffer?.recurringAmount;
    newContractTerms.contractTerms.commissionAmount = acceptedOffer?.commissionAmount;
    newContractTerms.contractTerms.commissionPercentage = acceptedOffer?.commissionPercentage;
    newContractTerms.contractTerms.funder = { id: acceptedOffer?.funderId };
    newContractTerms.contractTerms.heldbackAmount = acceptedOffer?.otherFees;
    newContractTerms.contractTerms.numberOfDebits = acceptedOffer?.numberOfPayments;
    newContractTerms.numberOfDebits = acceptedOffer?.numberOfPayments;

    newContractTerms.contractTerms.fundedAmountChange = true;
    newContractTerms.contractTerms.commissionPercentageCalculationThrough = "commission_percentage";
    newContractTerms.contractTerms.fundingDate = acceptedOffer?.fundingDate;

    newContractTerms.fundedAmount = acceptedOffer?.fundingAmount;
    newContractTerms.purchaseAmount = acceptedOffer?.paybackAmount;
    newContractTerms.paybackFactor = acceptedOffer?.factor;
    newContractTerms.estimatedPayoffDate = acceptedOffer?.estimatedPayoffDate;
    newContractTerms.contractTerms.receivablePercentage = acceptedOffer?.specifiedPercentage;

    const refiDeals = newContractTerms.contractTerms.payingOffDeals;
    let newRefiDeals: any = [];

    const refinanceAmount = acceptedOffer?.refinanceAmount || 0;

    if (refinanceAmount > 0 && refiDeals && refiDeals.length > 0) {
      const refiDeal = refiDeals[0];
      refiDeal.payingoffAmount = refinanceAmount;
      newRefiDeals = [refiDeal];
    } else {
      newRefiDeals = [];
    }

    if (newRefiDeals && newRefiDeals.length > 0) {
      newContractTerms.contractTerms.parentDealAmount = refinanceAmount;
      newContractTerms.contractTerms.payingOffDeals = newRefiDeals;
    } else {
      newContractTerms.contractTerms.parentDealAmount = 0;
      newContractTerms.contractTerms.payingOffDeals = [];
    }

    //handle external payoff
    if (acceptedOffer.externalPayOffAmount && acceptedOffer.externalPayOffAmount > 0) {
      const defaultExternalFunder = externalFunders[0];
      if (defaultExternalFunder) {
        newContractTerms.contractTerms.externalAbaNumber = defaultExternalFunder?.externalAbaNumber;
        newContractTerms.contractTerms.externalAccountNumber =
          defaultExternalFunder?.externalAccountNumber;
        newContractTerms.contractTerms.externalAmount = acceptedOffer?.externalPayOffAmount;
        newContractTerms.contractTerms.externalBankName = defaultExternalFunder?.externalBankName;
        newContractTerms.contractTerms.externalFunderId = defaultExternalFunder?.externalFunderId;
        newContractTerms.contractTerms.mcaCompany = defaultExternalFunder?.mcaCompany;
      }
    } else {
      newContractTerms.contractTerms.externalFunderId = null;
    }

    await saveFinalOffer(newContractTerms);

    //heldbacks

    const otherFees = heldbacksData.filter((fee: any) => fee.type != "bank_fee");

    if (otherFees.length > 0) {
      if (otherFees.length > 0 && acceptedOffer?.otherFees && acceptedOffer?.otherFees > 0) {
        const fee = otherFees[0];
        fee.amount = acceptedOffer?.otherFees;
        fee.partyId = fee?.applicationPartyId;

        if (otherFees.length > 1) {
          for (let i = 1; i < otherFees.length; i++) {
            let remainingHeldBack = otherFees[i];
            await deleteHeldBack({
              id: remainingHeldBack.id,
              type: "",
              applicationId: dealApplicationId,
              deletingIds: [],
              callback: undefined,
            });
          }
        }

        await saveHeldback({
          offerDetails: newContractTerms,
          heldbackData: fee,
          dealApplicationId: dealApplicationId,
          callback: undefined,
        });
      } else {
        for (let i = 0; i < otherFees.length; i++) {
          let remainingHeldBack = otherFees[i];
          await deleteHeldBack({
            id: remainingHeldBack.id,
            type: "",
            applicationId: dealApplicationId,
            deletingIds: [],
            callback: undefined,
          });
        }
      }
    } else if (acceptedOffer?.otherFees && acceptedOffer?.otherFees > 0) {
      const fee = {
        amount: acceptedOffer?.otherFees,
        applicationId: dealApplicationId,
        description: "",
        partyId: acceptedOffer?.funderId || newContractTerms?.contractTerms?.funder?.funderId,
        tenantId: "1",
        type: "origination_fee",
      };
      await saveHeldback({
        offerDetails: newContractTerms,
        heldbackData: fee,
        dealApplicationId: dealApplicationId,
        callback: undefined,
      });
    }
  };

  const clearAcceptedOffer = async () => {
    const newContractTerms = contractTerms;
    newContractTerms.applicationId = dealApplicationId;
    newContractTerms.tenantId = 1;
    newContractTerms.bankFee = 0;
    newContractTerms.bankFeePercentage = 0;
    newContractTerms.commissionTotal = 0;

    newContractTerms.contractTerms.collectionFrequency = "";
    newContractTerms.contractTerms.collectionRecurringAmount = 0;
    newContractTerms.contractTerms.commissionAmount = 0;
    newContractTerms.contractTerms.commissionPercentage = 0;
    newContractTerms.contractTerms.heldbackAmount = 0;
    newContractTerms.contractTerms.numberOfDebits = 0;

    newContractTerms.numberOfDebits = 0;
    newContractTerms.contractTerms.receivablePercentage = 0;

    newContractTerms.fundedAmount = 0;
    newContractTerms.purchaseAmount = 0;
    newContractTerms.paybackFactor = 0;
    newContractTerms.estimatedPayoffDate = "";

    await saveFinalOffer(newContractTerms);
  };

  const handleSaveOffer = async (offer: any) => {
    try {
      setLoading(true);
      const { oldStatus, ...cleanedOffer } = offer;
      cleanedOffer["applicationId"] = dealApplicationId;

      const response = await addOffer(cleanedOffer);
      console.log("logging...", offer);
      if (response?.acceptedOffer) {
        await handleAcceptedOffer(response.acceptedOffer);
      } else if (oldStatus && oldStatus === "Accepted" && cleanedOffer?.status !== "Accepted") {
        await clearAcceptedOffer();
      }
      const offerDetails = await getContractTerms(dealApplicationId);

      await handleBankAmountUpdate({
        offerDetails: offerDetails,
        dealApplicationId: dealApplicationId,
      });
      await fetchRecalculatedApplication(dealApplicationId);
      await updateSyndicatorsPercentage({
        applicationId: dealApplicationId,
        reload: false,
      });
      await fetchOffers(dealApplicationId);
      await refreshHeldabckComm(dealApplicationId);
      await refreshSynd(dealApplicationId);
      setIsEditing(false);
      setEditingRow(null);
    } catch (error) {
      console.error("Error while saving offer:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (value: string, field: string) => {
    setEditingRow((prev: any) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSelectRow = (rowKey: string) => {
    if (selectedRows.includes(rowKey)) {
      setSelectedRows(selectedRows.filter((key: any) => key !== rowKey));
    } else {
      setSelectedRows([...selectedRows, rowKey]);
    }
  };

  const handleSelectAll = () => {
    if (selectedRows?.length === offers?.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(offers.map((row: any) => row.offerKey));
    }
  };

  const handleSort = (columnKey: string) => {
    const isAsc = sortedColumn === columnKey && sortDirection === "asc";
    setSortedColumn(columnKey);
    setSortDirection(isAsc ? "desc" : "asc");
  };

  const sortedOffersData = useMemo(() => {
    const statusOrder: any = {
      Accepted: 1,
      Sent: 2,
      "Not Sent": 3,
      Declined: 4,
    };

    if (!sortedColumn) return offers;

    const sortedData = [...offers].sort((a, b) => {
      if (sortedColumn === "status") {
        const statusA = a[sortedColumn];
        const statusB = b[sortedColumn];

        return statusOrder[statusA] - statusOrder[statusB];
      }

      if (a[sortedColumn] < b[sortedColumn]) return sortDirection === "asc" ? -1 : 1;
      if (a[sortedColumn] > b[sortedColumn]) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return sortedData;
  }, [offers, sortedColumn, sortDirection]);

  const getRowStyle = (row: any) => {
    if (row.status === "Accepted") {
      return "bold";
    }
    return "regular";
  };

  return (
    <>
      <CollapsibleCard header={"Offers"} customButton={customButton}>
        <MDBox paddingRight={3}>
          <OffersTable
            columns={columns}
            rowKey={"offerKey"}
            data={sortedOffersData || []}
            onDelete={handleDeleteOffer}
            onEdit={handleEditOffer}
            onSave={handleSaveOffer}
            onCancel={handleCancelEdit}
            editingRow={editingRow}
            isEditing={isEditing}
            onInputChange={handleInputChange}
            selectedRows={selectedRows}
            onSelectRow={handleSelectRow}
            onSelectAll={handleSelectAll}
            onSort={handleSort}
            getRowStyle={getRowStyle}
            isActionsDisabled={undefined}
            renderTotal={null}
            totalColumn={null}
          />
        </MDBox>
      </CollapsibleCard>

      {showSendOffer && (
        <GenericEmailSenderModal
          selected={selectedRows}
          header={"Send Offers"}
          dealApplicationId={dealApplicationId}
          topicName="event.deal.dealApplication.status.multiOffers"
          showModal={showSendOffer}
          onClose={() => setShowSendOffer(false)}
          refresh={onInit}
          onSend={() => console.log("onSend::")}
          isAutoSubmitted={false} 
          customTextProps={undefined} />
      )}

      {isViewEditMode && (
        <FundingChart
          mode={mode}
          setMode={setMode}
          open={isViewEditMode}
          setOpen={setIsViewEditMode}
          formData={formData}
          setFormData={setFormData}
          scoreboard={scoreboard}
          debits={debits}
          setDebits={setDebits}
          dealApplicationId={dealApplicationId}
          scheduleFrequency={scheduleFrequency}
          setScheduleFrequency={setScheduleFrequency}
        />
      )}

      {loading && <FullPageLoader />}
    </>
  );
};

export default Offers;
